/**
 * 「コンテンツを追加」ボタン群 (カテゴリ別に 4 つ)。
 *
 * 以前は「＋ コンテンツを追加」を押してから、追加された行のカテゴリ <select> で
 * 種類を選び直す 2 ステップの操作だった。
 * カテゴリごとのボタンを並べることで、1 クリックで目的の種類のコンテンツを追加できる。
 *
 * コンテンツが増えてもスクロールで見えなくならないよう、放送地点フォームのフッター
 * (保存 / キャンセルと同じ行) に配置する。
 * 加えて、コンテンツ一覧の見出し行 (「コンテンツ (N 件)」の右) にも同じボタン群を置く。
 * 同一画面に 2 組が並ぶため、`data-testid` / `aria-label` は `variant` で区別する。
 */
import type { BroadcastContentCategory } from '../../../api/types';

/** 配置場所。`data-testid` / `aria-label` の衝突を避けるために使い分ける。 */
export type AddContentButtonsVariant = 'footer' | 'header';

/** 新規追加に使うカテゴリ (旧 `connecting_line_delay` は新規作成の対象外)。 */
const ADD_BUTTONS: ReadonlyArray<{
  category: BroadcastContentCategory;
  /** ボタンに表示する短いラベル */
  buttonLabel: string;
  /** aria-label / title に使うカテゴリ名 (カテゴリ <select> の表示名と同じ) */
  categoryLabel: string;
}> = [
  { category: 'self_train_delay', buttonLabel: '自列車遅延', categoryLabel: '自列車遅延' },
  {
    category: 'connecting_line_delay_by_station',
    buttonLabel: '接続線遅延',
    categoryLabel: '接続線遅延',
  },
  { category: 'train_info', buttonLabel: '運行情報', categoryLabel: '運行情報' },
  { category: 'free_text', buttonLabel: '任意テキスト', categoryLabel: '任意テキストの放送' },
];

export function AddContentButtons({
  onAdd,
  variant = 'footer',
}: {
  /** 押されたカテゴリのコンテンツを末尾に追加する */
  onAdd: (category: BroadcastContentCategory) => void;
  /**
   * 配置場所 (既定: `'footer'`)。
   * - `'footer'`: `footer-add-content-button-<category>` (従来どおり)
   * - `'header'`: `header-add-content-button-<category>` (コンテンツ一覧の見出し行)
   */
  variant?: AddContentButtonsVariant;
}): React.JSX.Element {
  const testIdPrefix = `${variant}-add-content`;
  return (
    <div
      className="add-content-buttons"
      role="group"
      aria-label="コンテンツを末尾に追加"
      data-testid={`${testIdPrefix}-buttons`}
    >
      <span className="add-content-buttons__label">コンテンツを追加:</span>
      {ADD_BUTTONS.map((b) => (
        <button
          key={b.category}
          type="button"
          data-testid={`${testIdPrefix}-button-${b.category}`}
          aria-label={
            variant === 'header'
              ? `コンテンツを末尾に追加・上側（${b.categoryLabel}）`
              : `コンテンツを末尾に追加（${b.categoryLabel}）`
          }
          onClick={() => onAdd(b.category)}
        >
          ＋ {b.buttonLabel}
        </button>
      ))}
    </div>
  );
}
